import { Input } from '../../../../components/ui/input';
import { Dropdown, Button } from 'react-bootstrap';
import { Download } from 'lucide-react';

import { OrderDateRangePicker } from './OrderDateRangePicker';
import { CountryFilter } from './CountryFilter';
import { OrderStatusFilter } from './OrderStatusFilter';
import { PicFilter } from './PicFilter';
import { PageSizeSelector } from './PageSizeSelector';
import type { Column } from './Columns';

export interface OrdersTableFilterControlProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  allColumns: Column[];
  visibleColumns: Column[];
  onColumnVisibilityChange: (column: Column, isVisible: boolean) => void;
  startDate: string;
  endDate: string;
  onDateRangeChange: (startDate: string, endDate: string) => void;
  country: string;
  onCountryChange: (country: string) => void;
  status: string;
  onStatusChange: (status: string) => void;
  pic: string;
  onPicChange: (pic: string) => void;
  itemsPerPage: string;
  onItemsPerPageChange: (value: string) => void;
  hasOrderNumber: boolean;
  onHasOrderNumberChange: (value: boolean) => void;
  hasTrackingNumber: boolean;
  onHasTrackingNumberChange: (value: boolean) => void;
  onOpenParxlModal?: () => void;
}

export function OrdersTableFilterControl({
  searchTerm,
  onSearchChange,
  allColumns,
  visibleColumns,
  onColumnVisibilityChange,
  startDate,
  endDate,
  onDateRangeChange,
  country,
  onCountryChange,
  status,
  onStatusChange,
  pic,
  onPicChange,
  itemsPerPage,
  onItemsPerPageChange,
  hasOrderNumber,
  onHasOrderNumberChange,
  hasTrackingNumber,
  onHasTrackingNumberChange,
  onOpenParxlModal,
}: OrdersTableFilterControlProps) {
  return (
    <div className="d-flex align-items-center gap-2 flex-wrap w-full">
      <OrderDateRangePicker startDate={startDate} endDate={endDate} onDateRangeChange={onDateRangeChange} />

      <Input
        placeholder="Search..."
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        style={{ width: '150px', height: '38px' }}
      />

      <OrderStatusFilter status={status} onStatusChange={onStatusChange} />

      <PicFilter pic={pic} onPicChange={onPicChange} />

      <div className="d-flex align-items-center gap-3">
        <label className="d-flex align-items-center gap-1 mb-0" style={{ fontSize: '13px' }}>
          <input
            type="checkbox"
            checked={hasOrderNumber}
            onChange={(e) => onHasOrderNumberChange(e.target.checked)}
          />
          <span>Order#</span>
        </label>
        <label className="d-flex align-items-center gap-1 mb-0" style={{ fontSize: '13px' }}>
          <input
            type="checkbox"
            checked={hasTrackingNumber}
            onChange={(e) => onHasTrackingNumberChange(e.target.checked)}
          />
          <span>Tracking#</span>
        </label>
      </div>

      <CountryFilter country={country} onCountryChange={onCountryChange} />

      <PageSizeSelector itemsPerPage={itemsPerPage} onItemsPerPageChange={onItemsPerPageChange} />

      <Dropdown>
        <Dropdown.Toggle variant="outline-dark" className="bg-gray-50 hover:bg-gray-100">
          Columns
        </Dropdown.Toggle>
        <Dropdown.Menu className="p-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
          {allColumns.map((column) => (
            <div key={String(column.accessor)} className="d-flex align-items-center gap-2 p-1">
              <input
                type="checkbox"
                checked={visibleColumns.some((col) => col.accessor === column.accessor)}
                onChange={(e) => onColumnVisibilityChange(column, e.target.checked)}
                disabled={column.isRequired}
              />
              <span className={column.isRequired ? 'text-gray-400' : ''}>{column.header}</span>
            </div>
          ))}
        </Dropdown.Menu>
      </Dropdown>

      {onOpenParxlModal && (
        <Button variant="primary" onClick={onOpenParxlModal} className="d-flex align-items-center gap-2">
          <Download size={16} />
          Export
        </Button>
      )}
    </div>
  );
}
